import api from "@lib/api";
import { type QueryKey, useQuery } from "@tanstack/react-query";
import type { ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import { useCallback } from "react";
import type { TableResponse } from "./types";
import { buildApiUrl, combineFilters, normalizeApiPlatformResponse } from "./utils";

interface UseTableDataOptions {
  /** API resource path (e.g. "/api/personnes") */
  resource: string;
  /** React Query key for cache management */
  queryKey: QueryKey;
  /** Additional React Query options forwarded to useQuery */
  queryOptions?: Record<string, unknown>;
  /** Pagination state from TanStack Table */
  pagination: PaginationState;
  /** Sorting state from TanStack Table */
  sorting: SortingState;
  /** Server-side initial filters applied on every request */
  initialFilters?: Record<string, unknown>;
  /** Column filters from TanStack Table */
  columnFilters?: ColumnFiltersState;
  /** Global search text filter */
  globalFilter?: string;
  /** Maps TanStack column id -> backend OrderFilter property (sortField). Plan §4.7.2 */
  sortFieldMap?: Record<string, string>;
}

/**
 * Hook for fetching paginated, sorted, and filtered table data from the API.
 * Constructs the API URL from table state, normalises the API Platform response,
 * and returns a standard React Query result.
 */
export function useTableData<TData = Record<string, unknown>>({
  resource,
  queryKey,
  queryOptions = {},
  pagination,
  sorting,
  initialFilters = {},
  columnFilters = [],
  globalFilter = "",
  sortFieldMap = {},
}: UseTableDataOptions) {
  const fetcher = useCallback(
    async ({ signal }: { signal: AbortSignal }) => {
      const filters = combineFilters(initialFilters, columnFilters);

      const url = buildApiUrl(resource, pagination, sorting, filters, globalFilter, sortFieldMap);

      try {
        const response = await api.get(url, { signal });
        return normalizeApiPlatformResponse(response) as TableResponse<TData>;
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          throw error;
        }
      }
    },
    [resource, pagination, sorting, initialFilters, columnFilters, globalFilter, sortFieldMap],
  );

  const result = useQuery({
    queryKey,
    queryFn: fetcher,
    placeholderData: (previousData: TableResponse<TData> | undefined) => previousData,
    ...queryOptions,
  });

  return result;
}
